import { useMutation, useQueryClient } from '@tanstack/react-query';

import { providerService } from '@/services/provider-service';
import type {
  CreateProviderFeaturePayload,
  Feature,
} from '@/types/core/provider';
import { pollUntilResourceNewerThan } from '@/utils/poll';
import { type UUID, newUUID } from '@/utils/uuid';

import { providerMutationKeys, providerQueryKeys } from './query-keys';

/**
 * Hook to create or update a provider feature.
 * Returns a react-query mutation that accepts feature configuration.
 * Invalidates the provider features cache to trigger a refetch.
 *
 * @param featureId - When provided, the existing feature is updated (reconciled
 *   with the same ID). When omitted, a new UUID is generated (create).
 */
export function useCreateProviderFeature(
  tenantId?: UUID,
  providerId?: UUID,
  featureId?: UUID
) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: providerMutationKeys.createProviderFeature(
      tenantId,
      providerId,
      featureId
    ),
    mutationFn: async (payload: CreateProviderFeaturePayload) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!providerId) throw new Error('Missing provider id');

      const targetFeatureId = featureId ?? newUUID();
      const cachedFeature = featureId
        ? qc.getQueryData<Feature | null>(
            providerQueryKeys.featureDetail(tenantId, providerId, featureId)
          )
        : null;
      const minRev = cachedFeature?._rev ?? -2;

      const accepted = await providerService.reconcileProviderFeature(
        tenantId,
        providerId,
        targetFeatureId,
        {
          feature_name: payload.name,
          description: payload.description ?? '',
          options: payload.options ?? '',
          requirements: payload.requirements ?? [],
          credentials: payload.credentials?.map((credential) => ({
            name: credential.name,
            template_id: credential.templateId,
            template_name: credential.templateName,
          })),
        }
      );

      if (!accepted) {
        throw new Error('Failed to create provider feature');
      }

      await pollUntilResourceNewerThan(
        async () => {
          if (featureId) {
            return providerService.findProviderFeature(
              tenantId,
              providerId,
              targetFeatureId
            );
          }

          const page = await providerService.listProviderFeatures(
            tenantId,
            providerId
          );
          return (
            page.items.find((feature) => feature.id === targetFeatureId) ?? null
          );
        },
        minRev,
        { maxAttempts: 10, delayMs: 300 }
      );

      return accepted;
    },
    onSuccess: () => {
      // Provider feature mutations can create/update connector feature projections,
      // so invalidate all feature-query families for this tenant.
      qc.invalidateQueries({
        queryKey: providerQueryKeys.featureListByTenant(tenantId),
        refetchType: 'all',
      });
      qc.invalidateQueries({
        queryKey:
          providerQueryKeys.connectorFeaturesWithProviderOptionsByTenant(
            tenantId
          ),
        refetchType: 'all',
      });

      // Keep existing entity-level invalidations for detail views.
      qc.invalidateQueries({
        queryKey: providerQueryKeys.featureListByProvider(tenantId, providerId),
        refetchType: 'all',
      });
      qc.invalidateQueries({
        queryKey: providerQueryKeys.featureDetail(
          tenantId,
          providerId,
          featureId
        ),
        refetchType: 'all',
      });
      qc.invalidateQueries({
        queryKey: providerQueryKeys.detail(tenantId, providerId),
        refetchType: 'all',
      });
    },
  });
}
